// Utility functions for image upload and handling
import { S3Client, PutObjectCommand } from "@aws-sdk/client-s3";
import { CognitoIdentityClient, GetIdCommand } from "@aws-sdk/client-cognito-identity";
import { fromCognitoIdentityPool } from "@aws-sdk/credential-providers";
import config from '../../../../../../../shared/config/appConfig';

/**
 * S3 Bucket CORS Configuration
 * 
 * The following CORS policy must be configured on the S3 bucket to allow
 * direct browser uploads. This can be set via AWS Console, CLI, or CloudFormation.
 * 
 * CORS Policy JSON:
 * [
 *   {
 *     "AllowedHeaders": ["*"],
 *     "AllowedMethods": ["PUT", "POST", "GET", "HEAD"],
 *     "AllowedOrigins": ["http://localhost:5173", "https://dev.vishmaker.com"],
 *     "ExposeHeaders": ["ETag", "x-amz-request-id", "x-amz-id-2"],
 *     "MaxAgeSeconds": 3000
 *   }
 * ]
 * 
 * AWS CLI command to set CORS:
 * aws s3api put-bucket-cors --bucket <bucket-name> --cors-configuration file://cors.json
 * 
 * Or via AWS Console:
 * 1. Go to S3 bucket → Permissions → Cross-origin resource sharing (CORS)
 * 2. Paste the JSON configuration above
 */

export interface ImageUploadResult {
    url: string;
    mime: string;
    name: string;
    key?: string; // S3 key if uploaded to S3
}

/**
 * Convert a file to base64 data URL
 * For now, we'll use data URLs. Later we can add S3 upload functionality.
 */
export const convertFileToDataURL = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });
};

/**
 * Create a tiny thumbnail data URL for chat preview caching.
 * Keeps localStorage footprint small while preserving visible attachments after reload.
 */
export const createImageThumbnailDataURL = async (
    file: File,
    maxDimension: number = 80,
    quality: number = 0.72
): Promise<string> => {
    const objectUrl = URL.createObjectURL(file);
    try {
        const img = await new Promise<HTMLImageElement>((resolve, reject) => {
            const el = new Image();
            el.onload = () => resolve(el);
            el.onerror = reject;
            el.src = objectUrl;
        });

        const width = img.naturalWidth || img.width || maxDimension;
        const height = img.naturalHeight || img.height || maxDimension;
        const scale = Math.min(1, maxDimension / Math.max(width, height));
        const targetWidth = Math.max(1, Math.round(width * scale));
        const targetHeight = Math.max(1, Math.round(height * scale));

        const canvas = document.createElement('canvas');
        canvas.width = targetWidth;
        canvas.height = targetHeight;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
            throw new Error('Could not create canvas context');
        }
        ctx.drawImage(img, 0, 0, targetWidth, targetHeight);

        const normalizedMime = file.type && file.type.startsWith('image/') ? file.type : 'image/jpeg';
        return canvas.toDataURL(normalizedMime, quality);
    } finally {
        URL.revokeObjectURL(objectUrl);
    }
};

/**
 * Convert a data URL (base64 image) to a File object
 * Useful for converting screenshots or canvas images to files for S3 upload
 * @param dataUrl - Base64 data URL (e.g., "data:image/png;base64,...")
 * @param filename - Optional filename (default: "screenshot.png")
 * @returns File object that can be uploaded using uploadImage()
 */
export const convertDataURLToFile = (dataUrl: string, filename: string = 'screenshot.png'): File => {
    // Extract the base64 data and mime type from the data URL
    const arr = dataUrl.split(',');
    const mimeMatch = arr[0].match(/:(.*?);/);
    const mime = mimeMatch ? mimeMatch[1] : 'image/png';
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    
    while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
    }
    
    return new File([u8arr], filename, { type: mime });
};

/**
 * Convert a data URL to a Blob object
 * Useful for creating blob URLs from cached screenshots
 * @param dataUrl - Base64 data URL
 * @returns Blob object
 */
export const convertDataURLToBlob = (dataUrl: string): Blob => {
    const arr = dataUrl.split(',');
    const mimeMatch = arr[0].match(/:(.*?);/);
    const mime = mimeMatch ? mimeMatch[1] : 'image/png';
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    
    while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
    }
    
    return new Blob([u8arr], { type: mime });
};

/**
 * Decode JWT token to extract claims
 */
function decodeJWT(token: string): any {
    try {
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        }).join(''));
        return JSON.parse(jsonPayload);
    } catch (error) {
        console.error('Failed to decode JWT:', error);
        return null;
    }
}

/**
 * Get provider name for Cognito Identity Pool from ID token
 * The provider name should match the issuer in the ID token
 */
function getProviderNameFromToken(idToken: string, region: string, userPoolId: string): string {
    // Try to get issuer from token first
    const decoded = decodeJWT(idToken);
    if (decoded && decoded.iss) {
        // Extract the user pool ID from the issuer
        // Issuer format: https://cognito-idp.{region}.amazonaws.com/{userPoolId}
        const issuer = decoded.iss;
        // Remove https:// prefix if present
        const providerName = issuer.replace('https://', '');
        console.log('📋 Using provider name from token issuer:', providerName);
        return providerName;
    }
    
    // Fallback to constructing it
    const fallback = `cognito-idp.${region}.amazonaws.com/${userPoolId}`;
    console.log('📋 Using fallback provider name:', fallback);
    return fallback;
}

/**
 * Normalize file extension to lowercase and validate it's an image extension
 */
function normalizeImageExtension(file: File): string {
    const extension = (file.name.split('.').pop() || 'png').toLowerCase();
    const validExtensions = ['png', 'jpg', 'jpeg', 'gif', 'webp'];
    
    // Map common extensions to standard ones
    const extensionMap: Record<string, string> = {
        'jpg': 'jpeg',
        'jpeg': 'jpeg',
        'png': 'png',
        'gif': 'gif',
        'webp': 'webp'
    };
    
    // Check if extension is valid, default to png if not
    return extensionMap[extension] || 'png';
}

function compactPathSegment(value: string | number | undefined, fallback: string): string {
    const raw = String(value ?? '').trim();
    if (!raw) return fallback;
    const normalized = raw.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
    if (!normalized) return fallback;
    return normalized.slice(0, 5);
}

function buildUploadTimestamp(): string {
    const now = new Date();
    const pad = (num: number) => String(num).padStart(2, '0');
    return [
        now.getUTCFullYear(),
        pad(now.getUTCMonth() + 1),
        pad(now.getUTCDate()),
        pad(now.getUTCHours()),
        pad(now.getUTCMinutes()),
        pad(now.getUTCSeconds())
    ].join('');
}

function sanitizeUploadedFileName(file: File): string {
    const rawName = String(file.name || '').trim();
    const fallbackExt = normalizeImageExtension(file);
    const fallbackName = `upload.${fallbackExt}`;
    if (!rawName) return fallbackName;

    const extFromFile = normalizeImageExtension(file);
    const nameWithoutExt = rawName.replace(/\.[^/.]+$/, '');
    const safeBase = nameWithoutExt
        .replace(/[^a-zA-Z0-9._-]+/g, '_')
        .replace(/_+/g, '_')
        .replace(/^[_\-.]+|[_\-.]+$/g, '')
        .toLowerCase();

    const base = safeBase || 'upload';
    return `${base}.${extFromFile}`;
}

/**
 * Upload image to S3 using Cognito Identity Pool credentials
 * @param file - Image file to upload
 * @param idToken - Cognito User Pool ID token (JWT)
 * @param userId - User ID for organizing uploads
 * @param projectId - Project ID for organizing uploads
 * @returns ImageUploadResult with S3 URL
 */
export async function uploadImageToS3(
    file: File,
    idToken: string,
    userId: string,
    projectId?: string | number
): Promise<ImageUploadResult> {
    const region = config.awsRegion;
    const identityPoolId = config.cognitoIdentityPoolId;
    const userPoolId = config.cognitoUserPoolId;
    const bucket = config.s3UserUploadBucket;

    console.log('🧪 S3 upload prerequisites presence check:', {
        has_region: Boolean(region && String(region).trim()),
        has_identity_pool_id: Boolean(identityPoolId && String(identityPoolId).trim()),
        has_user_pool_id: Boolean(userPoolId && String(userPoolId).trim()),
        has_bucket: Boolean(bucket && String(bucket).trim()),
        has_id_token: Boolean(idToken && String(idToken).trim()),
        has_user_id: Boolean(userId && String(userId).trim()),
    });

    // Validate and trim region first
    const trimmedRegion = region?.trim();
    if (!trimmedRegion || trimmedRegion === '') {
        console.error('❌ Region validation failed:', {
            region,
            type: typeof region,
            configAwsRegion: config.awsRegion,
            envVar: import.meta.env.VITE_AWS_REGION
        });
        throw new Error('AWS Region is missing. Please set VITE_AWS_REGION environment variable.');
    }

    // Validate all required configuration
    if (!identityPoolId) {
        throw new Error('Cognito Identity Pool ID is missing. Please set VITE_COGNITO_IDENTITY_POOL_ID environment variable.');
    }
    if (!userPoolId) {
        throw new Error('Cognito User Pool ID is missing. Please set VITE_COGNITO_USER_POOL_ID environment variable.');
    }
    if (!bucket) {
        throw new Error('S3 bucket is missing. Please set VITE_S3_USER_UPLOAD_BUCKET environment variable.');
    }

    if (!userId) {
        throw new Error('User ID is required for S3 upload');
    }

    console.log('🔧 S3 Upload Configuration:', {
        region: trimmedRegion,
        identityPoolId: identityPoolId.substring(0, 20) + '...',
        userPoolId,
        bucket,
        hasIdToken: !!idToken,
        userId
    });

    const providerName = getProviderNameFromToken(idToken, trimmedRegion, userPoolId);

    const cognitoIdentityClient = new CognitoIdentityClient({ region: trimmedRegion });
    
    // Get Identity ID from Cognito Identity Pool
    const getIdCommand = new GetIdCommand({
        IdentityPoolId: identityPoolId,
        Logins: {
            [providerName]: idToken,
        },
    });
    
    const identityResponse = await cognitoIdentityClient.send(getIdCommand);
    const identityId = identityResponse.IdentityId;
    
    // Log upload details
    console.log('📤 S3 Image Upload:', {
        userId,
        projectId: projectId || 'none',
        identityId: identityId || 'unknown',
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type,
    });
    
    console.log('🌍 Using AWS Region:', trimmedRegion);

    // Create credentials provider
    // The credentials provider needs a CognitoIdentityClient with the region set
    // We'll create one and pass it (even though TypeScript types might not show it, it's needed at runtime)
    const credentialsClient = new CognitoIdentityClient({ region: trimmedRegion });
    
    const credentials = fromCognitoIdentityPool({
        identityPoolId,
        // @ts-ignore - client parameter exists at runtime even if not in types
        client: credentialsClient,
        logins: {
            [providerName]: idToken, // IMPORTANT: use the ID token here
        },
    });

    let resolvedCredentials;
    try {
        console.log('🔑 Resolving credentials...');
        resolvedCredentials = await credentials();
        console.log('🔑 Credentials resolved successfully:', {
            hasAccessKeyId: !!resolvedCredentials.accessKeyId,
            hasSecretAccessKey: !!resolvedCredentials.secretAccessKey,
            hasSessionToken: !!resolvedCredentials.sessionToken,
            expiration: (resolvedCredentials as any).expiration
        });
    } catch (credError: any) {
        console.error('❌ Failed to resolve credentials:', credError);
        console.error('❌ Credentials error details:', {
            name: credError?.name,
            message: credError?.message,
            code: credError?.code,
            stack: credError?.stack
        });
        throw new Error(`Failed to get AWS credentials: ${credError?.message || credError || 'Unknown error'}`);
    }

    // Create S3Client with explicit region and credentials
    // The region must be a valid AWS region string
    if (!trimmedRegion || trimmedRegion.length === 0) {
        throw new Error('Region is required for S3Client');
    }

    console.log('📦 Creating S3Client with:', {
        region: trimmedRegion,
        regionType: typeof trimmedRegion,
        regionLength: trimmedRegion.length,
        hasCredentials: !!resolvedCredentials,
        credentialsType: typeof resolvedCredentials
    });

    // Ensure region is explicitly set as a string
    const s3Region: string = String(trimmedRegion).trim();
    
    if (!s3Region || s3Region.length === 0) {
        throw new Error(`Invalid region for S3Client: "${trimmedRegion}"`);
    }

    const s3 = new S3Client({ 
        region: s3Region, 
        credentials: resolvedCredentials
    });
    
    console.log('✅ S3Client created successfully with region:', s3Region);

    // Build S3 key: {userId[:5]}/{projectId[:5]}/{dtts}.{ext}
    const extension = normalizeImageExtension(file);
    const userSegment = compactPathSegment(userId, 'userx');
    const projectSegment = compactPathSegment(projectId, 'projx');
    const uploadFileName = sanitizeUploadedFileName(file);
    const key = `${userSegment}/${projectSegment}/${uploadFileName}`;

    console.log('📤 Uploading to S3:', {
        bucket,
        key,
        region: trimmedRegion,
        contentType: file.type || "image/png",
        fileSize: file.size,
        identityId
    });

    try {
        // Convert File to ArrayBuffer for browser compatibility
        // The AWS SDK in the browser needs ArrayBuffer instead of File directly
        const fileBuffer = await file.arrayBuffer();
        
        console.log('📋 File converted to ArrayBuffer, size:', fileBuffer.byteLength);
        
        const putCommand = new PutObjectCommand({
            Bucket: bucket,
            Key: key,
            Body: fileBuffer,
            ContentType: file.type || "image/png",
        });
        
        console.log('📋 PutObjectCommand created, sending to S3...');
        const response = await s3.send(putCommand);
        console.log('✅ S3 PutObjectCommand succeeded:', response);
    } catch (putError: any) {
        // Log the full error object
        console.error('❌ S3 PutObjectCommand failed - Full error:', putError);
        console.error('❌ Error details:', {
            name: putError?.name,
            message: putError?.message,
            code: putError?.code,
            $metadata: putError?.$metadata,
            stack: putError?.stack,
            toString: putError?.toString(),
            constructor: putError?.constructor?.name
        });
        
        // Try to extract more info
        if (putError?.message) {
            console.error('❌ Error message:', putError.message);
        }
        if (putError?.$metadata) {
            console.error('❌ Error metadata:', putError.$metadata);
        }
        
        throw putError;
    }

    // Construct the S3 URL
    // Encode the key but preserve forward slashes for path structure
    const url = `https://${bucket}.s3.${trimmedRegion}.amazonaws.com/${encodeURIComponent(key).replace(/%2F/g, "/")}`;
    console.log("S3 URL:", url);
    console.log("Origin:", window.location.origin);

    return {
        url,
        mime: file.type || 'image/png',
        name: file.name || 'image.png',
        key
    };
}

/**
 * Upload screenshot from cache (data URL) to S3
 * Converts data URL to File first, then uploads
 * @param dataUrl - Base64 data URL from screenshot cache
 * @param filename - Optional filename (default: "screenshot.png")
 * @param options - Optional parameters for S3 upload
 * @returns ImageUploadResult with S3 URL
 */
export const uploadScreenshotFromCache = async (
    dataUrl: string,
    filename: string = 'screenshot.png',
    options?: {
        idToken?: string;
        userId?: string;
        projectId?: string | number;
        useS3?: boolean;
    }
): Promise<ImageUploadResult> => {
    // Convert data URL to File
    const file = convertDataURLToFile(dataUrl, filename);
    
    // Use the regular uploadImage function
    return uploadImage(file, options);
};

/**
 * Upload image file and return URL
 * Tries S3 upload first if configured, otherwise falls back to data URL
 * @param file - Image file to upload
 * @param options - Optional parameters for S3 upload
 * @param options.idToken - Cognito ID token (required for S3 upload)
 * @param options.userId - User ID (required for S3 upload)
 * @param options.projectId - Project ID for organizing S3 uploads
 * @param options.useS3 - Force S3 upload (default: true if idToken and userId provided)
 */
export const uploadImage = async (
    file: File,
    options?: {
        idToken?: string;
        userId?: string;
        projectId?: string | number;
        useS3?: boolean;
    }
): Promise<ImageUploadResult> => {
    const { idToken, userId, projectId, useS3 = true } = options || {};

    // Try S3 upload if idToken and userId are provided and useS3 is true
    if (idToken && userId && useS3 && config.cognitoIdentityPoolId && config.s3UserUploadBucket) {
        try {
            return await uploadImageToS3(file, idToken, userId, projectId);
        } catch (error: any) {
            console.error('❌ S3 upload failed, falling back to data URL:', error);
            console.error('Error details:', {
                message: error.message,
                name: error.name,
                code: error.code
            });
            // Fall through to data URL fallback
        }
    }

    // Fallback to data URL
    const dataUrl = await convertFileToDataURL(file);
    
    return {
        url: dataUrl,
        mime: file.type || 'image/png',
        name: file.name || 'image.png'
    };
};

const PASTED_IMAGE_MIME_TO_EXT: Record<string, string> = {
    'image/jpeg': 'jpg',
    'image/jpg': 'jpg',
    'image/png': 'png',
    'image/gif': 'gif',
    'image/webp': 'webp',
};

function extensionForImageMime(mime: string): string {
    return PASTED_IMAGE_MIME_TO_EXT[mime.toLowerCase()] || 'png';
}

/**
 * Clipboard paste often yields unnamed or generic files; give a stable name for S3 keys.
 */
export function normalizePastedImageFile(file: File, index: number = 0): File {
    const mime = file.type && file.type.startsWith('image/') ? file.type : 'image/png';
    const ext = extensionForImageMime(mime);
    const baseName = file.name?.trim() || '';
    const hasMeaningfulName =
        baseName.length > 0 &&
        baseName !== 'image.png' &&
        baseName !== 'blob' &&
        !baseName.startsWith('blob:');

    if (hasMeaningfulName) {
        return file.type === mime ? file : new File([file], baseName, { type: mime });
    }

    const suffix = index > 0 ? `-${index + 1}` : '';
    const name = `pasted-screenshot-${Date.now()}${suffix}.${ext}`;
    return new File([file], name, { type: mime });
}

/**
 * Extract image files from a paste event (e.g. Cmd+V after screenshot).
 * Returns an empty array when the clipboard has no image items.
 */
export function getImageFilesFromClipboardEvent(event: ClipboardEvent): File[] {
    const items = event.clipboardData?.items;
    if (!items?.length) return [];

    const files: File[] = [];
    for (const item of Array.from(items)) {
        if (item.kind !== 'file') continue;
        const type = item.type || '';
        if (!type.startsWith('image/')) continue;
        const file = item.getAsFile();
        if (file) {
            files.push(normalizePastedImageFile(file, files.length));
        }
    }
    return files;
}

/**
 * Validate image file
 */
export const validateImageFile = (file: File): { valid: boolean; error?: string } => {
    const maxSize = 10 * 1024 * 1024; // 10MB
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    
    if (!allowedTypes.includes(file.type)) {
        return { valid: false, error: 'Invalid file type. Please upload an image (JPEG, PNG, GIF, or WebP).' };
    }
    
    if (file.size > maxSize) {
        return { valid: false, error: 'File size too large. Maximum size is 10MB.' };
    }
    
    return { valid: true };
};

